/**
 * Reads the colour mode the member chose in the application, which travels here in a cookie on the
 * parent domain.
 *
 * `localStorage` is per origin, and `app.` and `idp.` are different origins, so the choice cannot cross
 * that way: a member who picks dark in the application would still meet a white sign-in screen. A cookie
 * scoped to the parent domain is the only store both origins read without a round trip, and it has to be
 * readable from script - hence no HttpOnly. Nothing in it is a credential; it is which of two stylesheets
 * to use.
 *
 * The direction is one-way and this side is the reader. The application keeps `localStorage`
 * authoritative, because that is what MUI's own machinery reads and writes, and mirrors it outward into
 * this cookie. Behind both sits `members.themePreference`, the account-level record, which this side
 * cannot reach: the IdP has no session before authenticating and cannot read the application's database.
 * Each store answers a different question, which is what keeps them from disagreeing.
 */

// IMPORTANT: the application writes this name from another repository, so the string is duplicated across
// a repository boundary and nothing pins the two together - a rename on either side breaks this sign-in
// silently, in the direction that leaves the screen light while the member asked for dark. The pin that
// would close it is moving the name into `ui-styles`, which both repositories already install and both
// gates already read.
export const COLOR_MODE_COOKIE = 'lm-color-mode'

/**
 * An enum rather than a union of literals, so the set below is derived instead of written twice. It also
 * matches how the application frontend and the backend both declare these three, which matters because
 * the value crosses all three as a cookie and a column.
 */
export enum ColorMode {
  SYSTEM = 'system',
  LIGHT = 'light',
  DARK = 'dark',
}

const MODES: readonly string[] = Object.values(ColorMode)

/**
 * `dark` is the answer for a missing cookie, because it is the default of `members.themePreference` and
 * therefore what the member will be shown a moment after signing in. Falling back to `system` instead
 * would meet a member on a light device with a light sign-in screen and then turn the application dark,
 * once on every device they have not used before.
 *
 * IMPORTANT: this default is duplicated in three other places, and changing one without the others
 * produces exactly the flash this exists to remove. `verify:color-mode` pins the one it can reach - the
 * blocking script in this application's `index.html`, which is parsed as HTML and cannot import. The
 * other two are in the other repository: the column's default and the provider's `defaultMode`.
 *
 * Anything that is not one of the three is treated as missing. The cookie is writable by script on any
 * subdomain of the parent, so its value is input rather than state: a stray value has to resolve to a
 * scheme this application actually declares, not become an attribute nobody styled.
 */
export const readColorMode = (): ColorMode => {
  try {
    const raw = document.cookie
      .split('; ')
      .find((entry) => entry.startsWith(`${COLOR_MODE_COOKIE}=`))
      ?.slice(COLOR_MODE_COOKIE.length + 1)
    if (!raw) return ColorMode.DARK
    const value = decodeURIComponent(raw)
    return MODES.includes(value) ? (value as ColorMode) : ColorMode.DARK
  } catch {
    // Reading cookies throws outright when site data is blocked. Dark is the correct degradation: a
    // blocked store is indistinguishable from an empty one, and dark is what an empty one means.
    return ColorMode.DARK
  }
}
